import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import type { AdminApi, DependentView } from '../../api/client.ts';

const TYPE_LABEL: Record<string, string> = { segment: 'Segments', canvas: 'Canvas', campaign: 'Campaigns', environment: 'Environments' };
const STATUS_LABEL: Record<NonNullable<DependentView['status']>, string> = { active: 'active', scheduled: 'scheduled', paused: 'paused', draft: 'draft', inactive: 'stopped or archived' };
const VIA_LABEL: Record<string, string> = { audience: 'in the audience', exclusion: 'in an exclusion', attribute_ref: 'reads the attribute', event_ref: 'reads the event' };

/** One dependent: its name (a link when it has a page), status and how it refers. */
export function DependentItem({ d }: { d: DependentView }) {
  return (
    <li>
      {d.url ? <Link to={d.url}>{d.name}</Link> : d.name}
      {d.status && <span className={`tag ${d.status === 'active' ? '' : 'tag--neutral'}`}> {STATUS_LABEL[d.status]}</span>}
      <span className="hint"> {VIA_LABEL[d.via] ?? d.via}</span>
    </li>
  );
}

/** «Used by» (where-used, BB-47): the objects that refer to this one, grouped by type. */
export function WhereUsedPanel({ environmentId, objectType, objectId }: { environmentId: string; objectType: string; objectId: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const used = useQuery({ queryKey: ['where-used', environmentId, objectType, objectId], queryFn: () => api.whereUsed(environmentId, objectType, objectId) });
  return (
    <section aria-label="Used by" className="where-used">
      <h3>Used by</h3>
      {used.isPending ? (
        <p aria-busy="true">Loading…</p>
      ) : used.isError ? (
        <p role="alert">Where-used could not be loaded.</p>
      ) : used.data.groups.length === 0 ? (
        <p className="hint">Nothing uses it yet.</p>
      ) : (
        used.data.groups.map((g) => (
          <div key={g.objectType}>
            <h4>{TYPE_LABEL[g.objectType] ?? g.objectType}</h4>
            <ul aria-label={TYPE_LABEL[g.objectType] ?? g.objectType}>
              {g.items.map((d) => (
                <DependentItem key={`${d.objectType}:${d.objectId}`} d={d} />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}
